import { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { useInjectSaga } from 'utils/redux-injectors';
import { preloadStateSaga } from './saga';
import { PreloadStateState } from './types';
import {
  ContentArrayPayload,
  UserSegmentPayload,
  UserMetaDataTypesPayload,
  SubAddOnTypesPayload,
  SubPlanTypePayload,
  CompanySsoConnectionTypePayload,
} from 'types/models';
import {
  failureStateUpdate,
  initialStateUpdate,
  successStateUpdate,
} from 'utils/reducer-helper/stateUpdate';
import { ILoadingStateWithData } from 'types/reducerState';

const initialPropertyState: ILoadingStateWithData<any> = {
  loading: true,
  error: false,
  data: null,
};

export const initialState: PreloadStateState = {
  userRole: initialPropertyState,
  userSegment: initialPropertyState,
  userMetaData: initialPropertyState,
  subAddon: initialPropertyState,
  subPlanType: initialPropertyState,
  companySsoConnectionType: initialPropertyState,
};

export const slice = createSlice({
  name: 'preloadState',
  initialState,
  reducers: {
    fetchCompanySsoConnectionType(state) {
      initialStateUpdate(state.companySsoConnectionType, null);
    },
    fetchCompanySsoConnectionTypeSucceed(
      state,
      action: PayloadAction<
        ContentArrayPayload<CompanySsoConnectionTypePayload>
      >,
    ) {
      successStateUpdate(state.companySsoConnectionType, action);
    },
    fetchCompanySsoConnectionTypeFailed(state) {
      failureStateUpdate(state.companySsoConnectionType, null);
    },
    fetchUserRole(state) {
      initialStateUpdate(state.userRole, null);
    },
    fetchUserRoleSucceed(
      state,
      action: PayloadAction<ContentArrayPayload<string>>,
    ) {
      successStateUpdate(state.userRole, action);
    },
    fetchUserRoleFailed(state) {
      failureStateUpdate(state.userRole, null);
    },
    fetchUserSegment(state) {
      initialStateUpdate(state.userSegment, null);
    },
    fetchUserSegmentSucceed(
      state,
      action: PayloadAction<ContentArrayPayload<UserSegmentPayload>>,
    ) {
      successStateUpdate(state.userSegment, action);
    },
    fetchUserSegmentFailed(state) {
      failureStateUpdate(state.userSegment, null);
    },
    fetchUserMetaDataTypes(state) {
      initialStateUpdate(state.userMetaData, null);
    },
    fetchUserMetaDataTypesSucceed(
      state,
      action: PayloadAction<ContentArrayPayload<UserMetaDataTypesPayload>>,
    ) {
      successStateUpdate(state.userMetaData, action);
    },
    fetchUserMetaDataTypesFailed(state) {
      failureStateUpdate(state.userMetaData, null);
    },
    fetchSubAddon(state) {
      initialStateUpdate(state.subAddon, null);
    },
    fetchSubAddonSucceed(
      state,
      action: PayloadAction<ContentArrayPayload<SubAddOnTypesPayload>>,
    ) {
      successStateUpdate(state.subAddon, action);
    },
    fetchSubAddonFailed(state) {
      failureStateUpdate(state.subAddon, null);
    },
    fetchSubPlanType(state) {
      initialStateUpdate(state.subPlanType, null);
    },
    fetchSubPlanTypeSucceed(
      state,
      action: PayloadAction<ContentArrayPayload<SubPlanTypePayload>>,
    ) {
      successStateUpdate(state.subPlanType, action);
    },
    fetchSubPlanTypeFailed(state) {
      failureStateUpdate(state.subPlanType, null);
    },
    cleanup: () => initialState,
  },
});

export const { actions: preloadStateActions, reducer: preloadStateReducer } =
  slice;

export const usePreloadStateSlice = () => {
  useInjectSaga({ key: slice.name, saga: preloadStateSaga });
  return { actions: slice.actions };
};

/**
 * Example Usage:
 *
 * export function MyComponentNeedingThisSlice() {
 *  const { actions } = usePreloadStateSlice();
 *
 *  const onButtonClick = (evt) => {
 *    dispatch(actions.someAction());
 *   };
 * }
 */
